import { useEffect } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { CalendarDays, Tag, Loader2 } from "lucide-react"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getPublishedBlogs,
  selectPublishedBlogs,
  selectPublishedBlogsLoading,
  selectPublishedBlogsError,
} from "@/store/slices/blogSlice"
import Loader from "@/components/loader/loader"

export default function BlogListing() {
  const dispatch = useAppDispatch()
  const blogPosts = useAppSelector(selectPublishedBlogs)
  const loading = useAppSelector(selectPublishedBlogsLoading)
  const error = useAppSelector(selectPublishedBlogsError)

  useEffect(() => {
    dispatch(getPublishedBlogs())
  }, [dispatch])

  if (loading) {
    return (
      <>
        <Seo {...routeSeoConfig.blog} type="website" />
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="flex justify-center items-center min-h-[400px]">
            <Loader />
          </div>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <Seo {...routeSeoConfig.blog} type="website" />
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => dispatch(getPublishedBlogs())}>Try Again</Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Seo {...routeSeoConfig.blog} type="website" />
      <div className="container mx-auto px-4 py-8 md:py-12">
        <h1 className="text-4xl font-bold text-center text-primary mb-8 md:mb-12">Our Blog</h1>
        <p className="text-center text-lg text-gray-600 max-w-2xl mx-auto mb-12">
          Stay updated with the latest skin and hair care tips from experts.
        </p>

        {blogPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No blog posts available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Card
                  key={post.id}
                  className="flex flex-col overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 pt-0"
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <img
                      src={post.image || "/placeholder.svg"}
                      alt={post.title}
                      width={400}
                      height={250}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = "/placeholder.svg";
                      }}
                    />
                  </Link>
                  <CardHeader className="pb-2 pt-4">
                    <CardTitle className="text-xl font-semibold text-primary line-clamp-2">
                      <Link to={`/blog/${post.slug}`} className="hover:text-secondary transition-colors">
                        {post.title}
                      </Link>
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-500 flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      <span>{post.date}</span>
                      <Tag className="w-4 h-4 ml-auto" />
                      <Badge variant="secondary" className="bg-secondary/20 text-secondary-foreground">
                        {post.category}
                      </Badge>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <p className="text-gray-700 line-clamp-3">{post.description}</p>
                  </CardContent>
                  <CardFooter>
                    <Link to={`/blog/${post.slug}`} className="w-full">
                      <Button
                        variant="outline"
                        className="w-full border-primary text-primary hover:bg-primary hover:text-white bg-transparent"
                      >
                        Read More
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>

            {/* Placeholder for pagination if needed for many posts */}
            {blogPosts.length > 6 && (
              <div className="flex justify-center mt-12">
                <Button variant="outline">View More Posts</Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
